import clientAxios from "../config";

/**
 * Listado paginado de facturas. Soporta búsqueda y filtros server-side.
 *
 * @param {string} token
 * @param {object} params  per_page, page, search, invoice_type, invoice_origin,
 *                         transaction_origin, transaction_response, status,
 *                         date_from, date_to
 */
const getAllInvoices = async (token, params = {}) => clientAxios.get(`/api/invoice`, {
    params,
    headers: {
        Authorization: `Bearer ${token}`,
    },
});

/**
 * Detalle de una factura por uuid (route binding implícito por uuid en el back).
 */
const getInvoice = async (token, uuid) => clientAxios.get(`/api/invoice/${uuid}`, {
    headers: {
        Authorization: `Bearer ${token}`,
    },
});

/**
 * Descarga el PDF de una factura como blob (responseType: 'blob').
 * Acceso: admin o dueño de la factura (validado por policy en el back).
 */
const downloadInvoicePdf = async (token, uuid) => clientAxios.get(`/api/invoice/${uuid}/pdf`, {
    responseType: 'blob',
    headers: {
        Authorization: `Bearer ${token}`,
    },
});

/**
 * Helper de UI: descarga el PDF de la factura y dispara el "Save as" del
 * navegador. Usar dentro de handlers de botón.
 */
const triggerInvoicePdfDownload = async (token, uuid, filename) => {
    const response = await downloadInvoicePdf(token, uuid);
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename ?? `factura-${uuid}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Creación manual de factura. Solo admin (validado por policy en el back).
 */
const createInvoice = async (token, data) => clientAxios.post(`/api/invoice`, data, {
    headers: {
        Authorization: `Bearer ${token}`,
    },
});

/**
 * Edición conservadora de factura: solo metadata
 * (description, expiration_time, redirect_url, status).
 */
const updateInvoice = async (token, uuid, data) => clientAxios.put(`/api/invoice/${uuid}`, data, {
    headers: {
        Authorization: `Bearer ${token}`,
    },
});

/**
 * Soft delete (status = 0).
 */
const deleteInvoice = async (token, uuid) => clientAxios.delete(`/api/invoice/${uuid}`, {
    headers: {
        Authorization: `Bearer ${token}`,
    },
});

/**
 * Reconcilia manualmente una factura WOMPI contra la API de Wompi.
 * Mismo motor que el cron `wompi:reconcile-pending`, útil para forzar
 * la actualización de una factura puntual desde el panel admin sin
 * esperar la siguiente corrida del scheduler.
 */
const reconcileInvoice = async (token, uuid) => clientAxios.post(`/api/invoice/${uuid}/reconcile`, {}, {
    headers: {
        Authorization: `Bearer ${token}`,
    },
});

/**
 * Aprueba manualmente una factura FENABOCOL (transition a 'APPROVED').
 * Solo admin. No dispara payout — es un cambio de estado contable.
 */
const approveInvoice = async (token, uuid) => clientAxios.post(`/api/invoice/${uuid}/approve`, {}, {
    headers: {
        Authorization: `Bearer ${token}`,
    },
});

export {
    getAllInvoices,
    getInvoice,
    downloadInvoicePdf,
    triggerInvoicePdfDownload,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    reconcileInvoice,
    approveInvoice,
};
